import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { LoadOption } from '../../types'

import { LoadsInput } from './components/LoadsInput'

export interface LoadListProps {
  testId?: string
  label: string
  options: LoadOption[]
  maxSelectionsCount?: number
}

export type LoadListFieldValue = Record<string, number>
const defaultComponentValue: LoadListFieldValue = {}

type LoadListFieldRecord = Record<string, number>
type LoadListFieldRecordOnChange = (value: LoadListFieldRecord) => void

export type LoadListFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = LoadListFieldValue,
> = {
  loadListProps: LoadListProps
} & TidalFormBridgeOptions<LoadListFieldValue, TargetField, State, AcceptedFormValue>

export function LoadListField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = LoadListFieldValue,
>(props: LoadListFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    loadListProps: { testId, label, options, maxSelectionsCount },
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      value,
      onChange,
      fieldDisabled,
      errorOrHelperText,
      showErrorAccent,
      fieldHasError,
    },
  } = useTidalFormBridge<LoadListFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  return (
    <LoadsInput
      testId={testId}
      label={label}
      value={value as LoadListFieldRecord}
      disabled={fieldDisabled}
      onChange={onChange as LoadListFieldRecordOnChange}
      error={showErrorAccent && fieldHasError}
      helperText={errorOrHelperText}
      options={options}
      maxSelectionsCount={maxSelectionsCount}
    />
  )
}
